import { Box, Button, ColumnLayout, Modal, SpaceBetween } from '@amzn/awsui-components-react-v3';
import React, { useEffect, useState } from 'react';
import {
  DATAZONE_TYPE,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  FGA_POLICY_TYPE_PREFIX,
  GALAXI_DATASOURCE_ID,
  GALAXI_TYPE,
  RESOURCE_ITEM,
  TAG_ITEM,
} from 'src/commons/constants';
import { getGalaxiSubscriptionURL } from 'src/components/workspaces/common/common';
import { GalaxiGroup } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { isDWaaSGroup } from 'src/api/config';
import { CartAccessItem } from 'src/components/common/checkout/common';
import { FgapAccessSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/fgapAccessSelector';
import { getIdInfoFromId } from 'src/components/utils/arnUtil';
import { DataZoneAccessSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/dataZoneAccessSelector';
import { TagAccessSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/tagAccessSelector';
import { ResourceLevelAccessSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/resourceLevelAccessSelector';
import { GalaxiAccessSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/galaxiAccessSelector';
import { PermissionDropdownOption } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { GalaxiGroupDropdown } from 'src/components/catalog/datasetdetail/accessSelectModal/galaxiGroupDropdown';

export interface AccessSelectModalProps {
  activeGroup: string;
  activeWorkspace?: any;
  visible: boolean;
  loading: boolean;
  dismiss: any;
  dataset: any;
  addToCart: (items: CartAccessItem[]) => void;
  catalogName: any;
  galaxiGroups?: GalaxiGroup[];
  selectedPermissionType?: any;
  allPermissionDropdownOptions: PermissionDropdownOption[];
}

export const AccessSelectModal = (props: AccessSelectModalProps) => {
  const [selectedGalaxiPermission, setSelectedGalaxiPermission] = useState<PermissionDropdownOption>(undefined);
  const [galaxiGroupErrorText, setGalaxiGroupErrorText] = useState<string>(undefined);
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);

  const [cartItemsToAdd, setCartItemsToAdd] = useState<CartAccessItem[]>([]);

  const datasetId = props.dataset?.Id;
  const idInfo = getIdInfoFromId(datasetId);

  const groupServiceSupported = props.galaxiGroups != null;
  const dataSourceIsGalaxi = idInfo.dataSource == GALAXI_DATASOURCE_ID;
  const onboardedToGalaxi = props.galaxiGroups?.length > 0;
  const isGalaxiPermission = props.selectedPermissionType == GALAXI_TYPE;
  const isRedshift = props.dataset?.ClusterIdentifier || props.dataset?.RedshiftWorkgroupName;
  const isDataZone =
    props.dataset?.DataSourceType == DZ_REDSHIFT_DATASOURCE_ID ||
    props.dataset?.DataSourceType == DZ_GLUE_DATASOURCE_ID;

  let buttonText = 'Add to cart';
  if (dataSourceIsGalaxi) {
    if (groupServiceSupported && !onboardedToGalaxi) {
      buttonText = 'Onboard to Galaxi';
    } else {
      buttonText = 'Request access';
    }
  } else if (isGalaxiPermission) {
    buttonText = 'Cut access ticket to Galaxi support team';
  }

  const handleConfirmForGalaxiResource = async () => {
    if (dataSourceIsGalaxi) {
      if (groupServiceSupported && !onboardedToGalaxi) {
        window.open('https://w.amazon.com/bin/view/AWS/WWRO/BigDataPlatform/GalaxiAccessManager/Onboarding/', '_blank');
      } else if (selectedGalaxiPermission) {
        //selected group is selectedPermission?.value
        //need to append it to url
        window.open(getGalaxiSubscriptionURL(idInfo.catalogId, idInfo.databaseName, idInfo.tableName), '_blank');
      } else {
        window.open(getGalaxiSubscriptionURL(idInfo.catalogId, idInfo.databaseName, idInfo.tableName), '_blank');
      }
      return;
    }
  };

  const handleConfirm = async () => {
    // For most resource types (resource / FGAP / tag / DataZone), we add a resource to the cart.
    if (cartItemsToAdd?.length > 0) {
      props.addToCart(cartItemsToAdd);
      props.dismiss();
      return;
    }

    // handle Galaxi:
    if (isGalaxiPermission) {
      handleConfirmForGalaxiResource();
    }

    // handle DWaaS: (not sure if this branch is ever used)
    if (
      !isRedshift &&
      (selectedGalaxiPermission == undefined || (isDWaaSGroup(props.activeGroup) && !doesSelectedGroupBelongToGalaxi()))
    ) {
      return;
    }

    props.dismiss();
  };

  const doesSelectedGroupBelongToGalaxi = () => {
    let errorText = undefined;
    if (isDWaaSGroup(props.activeGroup)) {
      if (!selectedGalaxiGroup) {
        errorText = 'You must choose a Galaxi group to continue with your subscription';
      }
    }
    setGalaxiGroupErrorText(errorText);
    return errorText === undefined;
  };

  const getDropDownItemsBasedOnSelectionType = () => {
    if (props.selectedPermissionType) {
      if ([TAG_ITEM, FGA_POLICY_TYPE_PREFIX, GALAXI_TYPE, DATAZONE_TYPE].includes(props.selectedPermissionType)) {
        return props.allPermissionDropdownOptions?.filter((dropdownOption) => {
          return dropdownOption.type == props.selectedPermissionType;
        });
      } else {
        return props.allPermissionDropdownOptions?.filter((dropdownOption) => {
          return dropdownOption.type == RESOURCE_ITEM;
        });
      }
    }
  };

  const disableSubmitButton = () => {
    if (isGalaxiPermission) {
      return false;
    }
    return cartItemsToAdd.length == 0;
  };

  useEffect(() => {
    getDropDownItemsBasedOnSelectionType();
    setSelectedGalaxiPermission(undefined);
  }, [props.selectedPermissionType]);

  const options = getDropDownItemsBasedOnSelectionType();

  const galaxiGroupOptions = props.allPermissionDropdownOptions?.filter(
    (dropdownOption) => dropdownOption.type == GALAXI_TYPE,
  );

  const isDWaaSWorkspace = isDWaaSGroup(props.activeGroup);

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={props.dismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleConfirm} disabled={disableSubmitButton()}>
              {buttonText}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
      header={'Subscribe to dataset'}
    >
      <ColumnLayout>
        {props.selectedPermissionType && !isGalaxiPermission && (
          <>
            {props.selectedPermissionType == TAG_ITEM && (
              <TagAccessSelector
                activeGroup={props.activeGroup}
                activeWorkspace={props.activeWorkspace}
                idInfo={idInfo}
                loading={props.loading}
                options={options}
                setCartItemsToAdd={setCartItemsToAdd}
                isDWaaSWorkspace={isDWaaSWorkspace}
                galaxiGroupOptions={galaxiGroupOptions}
              />
            )}
            {props.selectedPermissionType == RESOURCE_ITEM && (
              <ResourceLevelAccessSelector
                activeGroup={props.activeGroup}
                activeWorkspace={props.activeWorkspace}
                idInfo={idInfo}
                loading={props.loading}
                options={options}
                setCartItemsToAdd={setCartItemsToAdd}
                isDWaaSWorkspace={isDWaaSWorkspace}
                galaxiGroupOptions={galaxiGroupOptions}
              />
            )}
            {props.selectedPermissionType == FGA_POLICY_TYPE_PREFIX && (
              <FgapAccessSelector
                activeWorkspace={props.activeWorkspace}
                idInfo={idInfo}
                options={props.allPermissionDropdownOptions?.filter((dropdownOption) => {
                  return dropdownOption.type == props.selectedPermissionType;
                })}
                loading={props.loading}
                setCartItemsToAdd={setCartItemsToAdd}
                isDWaaSWorkspace={isDWaaSWorkspace}
                galaxiGroupOptions={galaxiGroupOptions}
              />
            )}
          </>
        )}

        {isGalaxiPermission && (
          <GalaxiAccessSelector
            galaxiGroups={props.galaxiGroups}
            selectedGalaxiGroup={selectedGalaxiGroup}
            setSelectedGalaxiGroup={setSelectedGalaxiGroup}
            selectedPermission={selectedGalaxiPermission}
            setSelectedPermission={setSelectedGalaxiPermission}
            galaxiGroupErrorText={galaxiGroupErrorText}
            setGalaxiGroupErrorText={setGalaxiGroupErrorText}
            options={options}
            galaxiGroupOptions={props.allPermissionDropdownOptions?.filter(
              (dropdownOption) => dropdownOption.type == GALAXI_TYPE,
            )}
          />
        )}

        {selectedGalaxiPermission &&
          props.selectedPermissionType &&
          !isGalaxiPermission &&
          groupServiceSupported &&
          isDWaaSGroup(props.activeGroup) && (
            <GalaxiGroupDropdown
              selectedOption={selectedGalaxiGroup}
              onChange={(e) => {
                setGalaxiGroupErrorText(undefined);
                setSelectedGalaxiGroup(e.detail.selectedOption);
              }}
              options={props.allPermissionDropdownOptions?.filter(
                (dropdownOption) => dropdownOption.type == GALAXI_TYPE,
              )}
              errorText={galaxiGroupErrorText}
            />
          )}

        {isDataZone && options?.length > 0 && (
          <DataZoneAccessSelector
            loading={props.loading}
            idInfo={idInfo}
            setCartItemsToAdd={setCartItemsToAdd}
            permissionOption={options[0]}
            {...props}
          />
        )}
      </ColumnLayout>
    </Modal>
  );
};
